import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import SignupsTable from '../components/SignupsTable';
import Analytics from '../components/Analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('signups');
  const [signups, setSignups] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('admin_token');
    
    try {
      setLoading(true);
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const [signupsRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/signups`, config),
        axios.get(`${API}/analytics`, config)
      ]);

      setSignups(signupsRes.data);
      setAnalytics(analyticsRes.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
      } else {
        setError('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const handleExport = async () => {
    const token = localStorage.getItem('admin_token');
    
    try {
      const response = await axios.get(`${API}/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `colture-tour-signups-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('Failed to export data');
    }
  };

  const handleUpdateStatus = async (signupId, status) => {
    const token = localStorage.getItem('admin_token');
    
    try {
      await axios.patch(
        `${API}/signups/${signupId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh data
      fetchData();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleDelete = async (signupId) => {
    if (!window.confirm('Are you sure you want to delete this signup?')) {
      return;
    }

    const token = localStorage.getItem('admin_token');
    
    try {
      await axios.delete(`${API}/signups/${signupId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh data
      fetchData();
    } catch (err) {
      alert('Failed to delete signup');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Dashboard Header */}
      <div className="sticky-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <p className="caption-text text-muted-foreground mb-2">ADMIN DASHBOARD</p>
              <h1 className="section-title text-3xl md:text-4xl">TOUR MANAGEMENT</h1>
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleExport}
                className="btn-secondary"
                data-testid="export-btn"
              >
                EXPORT CSV
              </button>
              <button
                onClick={handleLogout}
                className="btn-primary"
                data-testid="logout-btn"
              >
                LOGOUT
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex gap-8">
            <button
              onClick={() => setActiveTab('signups')}
              className={`py-4 border-b-2 font-heading uppercase text-sm tracking-widest transition-colors ${
                activeTab === 'signups'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="signups-tab"
            >
              Signups ({signups.length})
            </button>
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-4 border-b-2 font-heading uppercase text-sm tracking-widest transition-colors ${
                activeTab === 'analytics'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
              data-testid="analytics-tab"
            >
              Analytics
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-12">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Loading...</p>
          </div>
        ) : error ? (
          <div className="bg-destructive/10 border border-destructive text-destructive px-6 py-4">
            <p>{error}</p>
          </div>
        ) : (
          <>
            {activeTab === 'signups' && (
              <SignupsTable
                signups={signups}
                onUpdateStatus={handleUpdateStatus}
                onDelete={handleDelete}
              />
            )}
            {activeTab === 'analytics' && (
              <Analytics analytics={analytics} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;